import React from 'react';
import { Sparkles, Brain } from 'lucide-react';
import { AvatarProfilesManager } from '../avatar/AvatarProfilesManager.jsx';
import { MultiAngleAvatarCard } from '../avatar/MultiAngleAvatarCard.jsx';
import { VoiceCloneCard } from '../avatar/VoiceCloneCard.jsx';
import { PromptInput } from '../prompt/PromptInput.jsx';
import { QualitySelector } from './QualitySelector.jsx';
import { RangeSlider } from './RangeSlider.jsx';
import { CameraSelector } from './CameraSelector.jsx';
import { AspectRatioSelector } from './AspectRatioSelector.jsx';
import { DurationAndFpsSelector } from './DurationAndFpsSelector.jsx';
import { GenerateButton } from '../generator/GenerateButton.jsx';
import { UpscalerControls } from './UpscalerControls.jsx';
import { RelightControls } from './RelightControls.jsx';
import { AutoEditorControls } from './AutoEditorControls.jsx';

export function StudioSidebar({
  activeTab,
  profiles,
  activeProfileId,
  setActiveProfileId,
  setProfiles,
  isDuoMode,
  setIsDuoMode,
  duoSecondaryId,
  setDuoSecondaryId,
  avatarImage,
  avatarName,
  avatarAngles,
  voiceName,
  voiceAudioUrl,
  setIsScannerModalOpen,
  setIsVoiceModalOpen,
  setIsCreativeModalOpen,
  prompt,
  setPrompt,
  negativePrompt,
  setNegativePrompt,
  handleMagicEnhance,
  isEnhancing,
  quality,
  setQuality,
  motionIntensity,
  setMotionIntensity,
  resemblance,
  setResemblance,
  creativity,
  setCreativity,
  cameraPreset,
  setCameraPreset,
  ratio,
  setRatio,
  duration,
  setDuration,
  fps,
  setFps,
  scaleFactor,
  setScaleFactor,
  lightColor,
  setLightColor,
  lightIntensity,
  setLightIntensity,
  lightDirection,
  setLightDirection,
  cutSilences,
  setCutSilences,
  neonCaptions,
  setNeonCaptions,
  captionAccent,
  setCaptionAccent,
  entranceZoom,
  setEntranceZoom,
  audioMaster,
  setAudioMaster,
  transcriptText,
  setTranscriptText,
  isGenerating,
  genProgress,
  handleGenerate,
  handleAutoEdit,
  afterImage,
  setBeforeImage,
  setAfterImage
}) {
  return (
    <aside style={{
      width: '420px',
      background: '#0f1118',
      borderRight: '1px solid rgba(255,255,255,0.08)',
      display: 'flex',
      flexDirection: 'column',
      padding: '20px',
      gap: '16px',
      overflowY: 'auto'
    }}>
      {/* Gestor de Biblioteca de Avatares / Perfiles */}
      <AvatarProfilesManager
        profiles={profiles}
        activeProfileId={activeProfileId}
        onSelectProfile={(id) => setActiveProfileId(id)}
        onCreateProfile={(name) => {
          const newP = {
            id: `p-${Date.now()}`,
            name,
            avatarSrc: '/avatar_base.jpg',
            angles: [],
            voiceAudioUrl: null,
            voiceName: `Voz de ${name}`
          };
          setProfiles((prev) => [...prev, newP]);
          setActiveProfileId(newP.id);
        }}
        onDeleteProfile={(id) => {
          if (profiles.length <= 1) return;
          setProfiles((prev) => prev.filter((p) => p.id !== id));
          if (activeProfileId === id) {
            const next = profiles.find((p) => p.id !== id);
            if (next) setActiveProfileId(next.id);
          }
        }}
        isVideoDuoMode={isDuoMode}
        onToggleDuoMode={setIsDuoMode}
        duoSecondaryProfileId={duoSecondaryId}
        onSelectDuoSecondary={setDuoSecondaryId}
      />

      {/* Avatar Multi-Ángulo del Perfil Activo */}
      <MultiAngleAvatarCard
        avatarSrc={avatarImage}
        avatarName={avatarName}
        angles={avatarAngles}
        onAvatarChange={(newSrc, fileName) => {
          const cleanName = fileName ? fileName.replace(/\.[^/.]+$/, '') : avatarName;
          setProfiles((prev) => prev.map((p) => (
            p.id === activeProfileId ? { ...p, avatarSrc: newSrc, name: cleanName } : p
          )));
        }}
        onAddAngle={(newAngle) => {
          setProfiles((prev) => prev.map((p) => (
            p.id === activeProfileId ? { ...p, angles: [...(p.angles || []), newAngle] } : p
          )));
        }}
        onRemoveAngle={(idx) => {
          setProfiles((prev) => prev.map((p) => (
            p.id === activeProfileId ? { ...p, angles: (p.angles || []).filter((_, i) => i !== idx) } : p
          )));
        }}
        onOpenScanner={() => setIsScannerModalOpen(true)}
      />

      {/* Clon de Voz IA del Perfil Activo */}
      <VoiceCloneCard
        voiceName={voiceName}
        isCloned={Boolean(voiceAudioUrl)}
        voiceAudioUrl={voiceAudioUrl}
        onOpenRecorder={() => setIsVoiceModalOpen(true)}
        onAudioUpload={(url, fileName) => {
          const cleanVoiceName = fileName ? fileName.replace(/\.[^/.]+$/, '') : `Voz de ${avatarName}`;
          setProfiles((prev) => prev.map((p) => (
            p.id === activeProfileId ? { ...p, voiceAudioUrl: url, voiceName: cleanVoiceName } : p
          )));
        }}
      />

      {/* Botón Director Creativo IA */}
      <button
        type="button"
        onClick={() => setIsCreativeModalOpen(true)}
        style={{
          width: '100%',
          padding: '10px 14px',
          borderRadius: '10px',
          background: 'linear-gradient(135deg, rgba(255, 145, 1, 0.15) 0%, rgba(168, 85, 247, 0.15) 100%)',
          border: '1px solid rgba(255, 145, 1, 0.4)',
          color: '#f8fafc',
          fontSize: '12px',
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          transition: 'all 0.2s',
          boxShadow: '0 4px 15px rgba(255, 145, 1, 0.1)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ background: '#ff9101', padding: '4px', borderRadius: '6px', color: '#000' }}>
            <Brain size={14} />
          </div>
          <div style={{ textAlign: 'left' }}>
            <div style={{ color: '#ff9101' }}>Director Creativo & Guionista IA</div>
            <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 500 }}>Diseña video y guion a partir de tu idea</div>
          </div>
        </div>
        <Sparkles size={15} color="#c084fc" />
      </button>

      {activeTab === 'video' && (
        <>
          <PromptInput
            prompt={prompt}
            onChangePrompt={setPrompt}
            negativePrompt={negativePrompt}
            onChangeNegativePrompt={setNegativePrompt}
            onMagicEnhance={handleMagicEnhance}
            isEnhancing={isEnhancing}
          />
          <QualitySelector quality={quality} onSelectQuality={setQuality} />
          <RangeSlider label="Intensidad de Movimiento" value={motionIntensity} min={1} max={10} unit=" / 10" leftHint="Sutil" rightHint="Dinámico" onChange={setMotionIntensity} />
          <RangeSlider label="Preservación Facial" value={resemblance} unit="%" leftHint="Flexible" rightHint="100% Idéntico" onChange={setResemblance} />
          <RangeSlider label="Creatividad Generativa" value={creativity} unit="%" onChange={setCreativity} />
          <CameraSelector currentPreset={cameraPreset} onSelectPreset={setCameraPreset} />
          <AspectRatioSelector currentRatio={ratio} onSelectRatio={setRatio} />
          <DurationAndFpsSelector duration={duration} onSelectDuration={setDuration} fps={fps} onSelectFps={setFps} />
          <GenerateButton isGenerating={isGenerating} progress={genProgress} onTriggerGenerate={handleGenerate} />
        </>
      )}

      {activeTab === 'upscaler' && (
        <UpscalerControls
          creativity={creativity}
          onChangeCreativity={setCreativity}
          resemblance={resemblance}
          onChangeResemblance={setResemblance}
          scaleFactor={scaleFactor}
          onChangeScaleFactor={setScaleFactor}
          onTriggerUpscale={handleGenerate}
          isProcessing={isGenerating}
        />
      )}

      {activeTab === 'relight' && (
        <RelightControls
          lightColor={lightColor}
          onChangeLightColor={setLightColor}
          lightIntensity={lightIntensity}
          onChangeLightIntensity={setLightIntensity}
          lightDirection={lightDirection}
          onChangeLightDirection={setLightDirection}
          onTriggerRelight={handleGenerate}
          isProcessing={isGenerating}
        />
      )}

      {activeTab === 'auto-edit' && (
        <AutoEditorControls
          cutSilences={cutSilences}
          onChangeCutSilences={setCutSilences}
          neonCaptions={neonCaptions}
          onChangeNeonCaptions={setNeonCaptions}
          captionAccent={captionAccent}
          onChangeCaptionAccent={setCaptionAccent}
          entranceZoom={entranceZoom}
          onChangeEntranceZoom={setEntranceZoom}
          audioMaster={audioMaster}
          onChangeAudioMaster={setAudioMaster}
          onTriggerAutoEdit={handleAutoEdit}
          isProcessing={isGenerating}
          transcriptText={transcriptText}
          onChangeTranscriptText={setTranscriptText}
          currentVideoUrl={afterImage}
          onVideoUploaded={(url) => {
            setBeforeImage(url);
            setAfterImage(url);
          }}
        />
      )}
    </aside>
  );
}
